/**
 * Payload-first inventory for the SVD bundle buy box — the V2 twin of `bundle-purchase.inventory.ts`.
 *
 * V2 moves inventory from a per-child `activeInventory` map onto each `VariantCore.activeInventory` (mapped
 * onto the domain `SvdProductVariant`), so this is a union of the single-product SVD seed
 * (`getSvdPayloadInventorySeed`) over every rendered constituent.
 *
 * ALL-OR-NOTHING, matching both V1 bundle and single-product V2: if ANY constituent can't produce a
 * complete seed, the whole thing returns `undefined` and the buy box falls back to the client
 * `/active-inventory` fan-out (`getProductsPurchaseInfo`) — the same path V1 uses. That fallback is the
 * expected day-one path: the captured V2 bundle response carries no `activeInventory` on any of its 562
 * variants (gap G2 in `master-plan.md`), so this seed stays inert until the BE populates it.
 *
 * Merge siblings are excluded on purpose (V1 parity): requiring their inventory up front would defeat the
 * seed, and a sibling swap fetches its own purchase info anyway.
 */

import { getSvdPayloadInventorySeed } from '@src/components/widgets/product-purchase-v2/product-purchase-v2.inventory'
import type { IProductPurchaseInfo } from '@src/quince-site-common/src/domains/ProductPurchaseInfo'
import type SvdProduct from '@src/quince-site-common/src/domains/svd/svd-product'
import type { InventoryResponse } from '@src/quince-site-common/src/services/inventoryService/inventory.service'

export interface SvdBundleInventorySeed {
  /** Per-child purchase info keyed by `product.id` — what `UPDATE_PRICING_INFO_FOR_ALL` expects. */
  purchaseInfo: Record<number, IProductPurchaseInfo>
  /** Union of every child's inventory, for the `inventory_data` sessionStorage contract. */
  inventoryMap: Record<string, InventoryResponse>
}

export function getSvdBundleInventorySeed(products: readonly SvdProduct[]): SvdBundleInventorySeed | undefined {
  if (products.length === 0) return undefined

  const purchaseInfo: Record<number, IProductPurchaseInfo> = {}
  const inventoryMap: Record<string, InventoryResponse> = {}

  for (const product of products) {
    const seed = getSvdPayloadInventorySeed(product)
    if (!seed) return undefined
    Object.assign(inventoryMap, seed.inventoryMap)
    purchaseInfo[Number(product.id)] = seed.purchaseInfo
  }

  return { purchaseInfo, inventoryMap }
}
